import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Localization } from 'connex-cds';
import { ViewerPageContainer, ViewerPageSizer, ViewerPageWrapper, ViewerRoot, ViewerToolbar } from './style';

const { Translate } = Localization ?? {};

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

const clampScale = (value = 1) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value ?? 1));

const getTouchDistance = (touches = []) => {
  const first = touches?.[0];
  const second = touches?.[1];
  if (!first || !second) {
    return 0;
  }
  const dx = (second?.clientX ?? 0) - (first?.clientX ?? 0);
  const dy = (second?.clientY ?? 0) - (first?.clientY ?? 0);
  return Math.hypot(dx, dy);
};

const getTouchMidpoint = (touches = []) => {
  const first = touches?.[0];
  const second = touches?.[1];
  if (!first || !second) {
    return null;
  }
  return {
    x: ((first?.clientX ?? 0) + (second?.clientX ?? 0)) / 2,
    y: ((first?.clientY ?? 0) + (second?.clientY ?? 0)) / 2,
  };
};

function Viewer({ src }) {
  const translateMessage = Localization?.useTranslateMessage?.();
  const [numPages, setNumPages] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [pinchFactor, setPinchFactor] = useState(1);
  const [pinchBox, setPinchBox] = useState(null);

  const pageContainerRef = useRef(null);
  const pageWrapperRef = useRef(null);
  const scaleRef = useRef(1);
  const pinchFactorRef = useRef(1);
  const pendingFocalScrollRef = useRef(null);
  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startScale: 1,
    contentX: 0,
    contentY: 0,
    viewportX: 0,
    viewportY: 0,
  });
  const panRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });

  scaleRef.current = scale ?? 1;
  pinchFactorRef.current = pinchFactor ?? 1;

  const displayedScale = clampScale((scale ?? 1) * (pinchFactor ?? 1));
  const zoomPercent = Math.round((displayedScale ?? 1) * 100);
  const isPinching = (pinchFactor ?? 1) !== 1;

  function onDocumentLoadSuccess(pdf = {}) {
    setNumPages(pdf?.numPages);
  }

  function nextPage() {
    setPageNumber(v => ++v);
  }

  function prevPage() {
    setPageNumber(v => --v);
  }

  function zoomIn() {
    setScale(current => Math.min((current ?? 1) + SCALE_STEP, MAX_SCALE));
  }

  function zoomOut() {
    setScale(current => Math.max((current ?? 1) - SCALE_STEP, MIN_SCALE));
  }

  const applyPendingFocalScroll = () => {
    const pending = pendingFocalScrollRef?.current;
    const container = pageContainerRef?.current;
    if (!pending || !container) {
      return;
    }
    // A live gesture or a newer scale owns the viewport, so the stored offsets no longer describe it.
    const isStale =
      pinchRef?.current?.active || panRef?.current?.active || (pending?.scale ?? 1) !== (scaleRef?.current ?? 1);
    if (isStale) {
      pendingFocalScrollRef.current = null;
      return;
    }
    container.scrollLeft = pending?.scrollLeft ?? 0;
    container.scrollTop = pending?.scrollTop ?? 0;
  };

  // The canvas keeps its old size until react-pdf finishes rendering at the new scale, so the
  // offsets get clamped here and only stick once onRenderSuccess fires. Keep them until then.
  const onPageRenderSuccess = () => {
    applyPendingFocalScroll();
    pendingFocalScrollRef.current = null;
  };

  useLayoutEffect(() => {
    applyPendingFocalScroll();
  }, [scale, pinchFactor]);

  useEffect(() => {
    const container = pageContainerRef?.current;
    if (!container) {
      return undefined;
    }

    const applyFocalScroll = (factor = 1) => {
      const gesture = pinchRef?.current ?? {};
      const scrollLeft = (gesture?.contentX ?? 0) * (factor ?? 1) - (gesture?.viewportX ?? 0);
      const scrollTop = (gesture?.contentY ?? 0) * (factor ?? 1) - (gesture?.viewportY ?? 0);
      container.scrollLeft = scrollLeft;
      container.scrollTop = scrollTop;
      return { scrollLeft, scrollTop };
    };

    const beginPinch = (touches = []) => {
      const distance = getTouchDistance(touches);
      if (!distance) {
        return;
      }

      const midpoint = getTouchMidpoint(touches);
      const rect = container?.getBoundingClientRect?.();
      const viewportX = (midpoint?.x ?? 0) - (rect?.left ?? 0);
      const viewportY = (midpoint?.y ?? 0) - (rect?.top ?? 0);
      const wrapper = pageWrapperRef?.current;

      panRef.current.active = false;
      pinchRef.current = {
        active: true,
        startDistance: distance,
        startScale: scaleRef?.current ?? 1,
        contentX: (container?.scrollLeft ?? 0) + viewportX,
        contentY: (container?.scrollTop ?? 0) + viewportY,
        viewportX,
        viewportY,
      };
      setPinchBox({
        width: wrapper?.offsetWidth ?? 0,
        height: wrapper?.offsetHeight ?? 0,
      });
    };

    const commitPinch = () => {
      if (!pinchRef?.current?.active) {
        return;
      }

      const nextScale = clampScale((pinchRef?.current?.startScale ?? 1) * (pinchFactorRef?.current ?? 1));
      const factor = nextScale / (pinchRef?.current?.startScale ?? 1);
      const focalScroll = applyFocalScroll(factor);
      // An unchanged scale never re-renders the page, so there is no onRenderSuccess to clear it.
      pendingFocalScrollRef.current =
        nextScale === (scaleRef?.current ?? 1) ? null : { ...focalScroll, scale: nextScale };
      pinchRef.current.active = false;
      pinchFactorRef.current = 1;
      setScale(nextScale);
      setPinchFactor(1);
      setPinchBox(null);
    };

    const onTouchStart = event => {
      const touches = event?.touches;
      if ((touches?.length ?? 0) >= 2) {
        event?.preventDefault?.();
        beginPinch(touches);
        return;
      }
      if ((touches?.length ?? 0) === 1) {
        const touch = touches?.[0];
        panRef.current = {
          active: true,
          startX: touch?.clientX ?? 0,
          startY: touch?.clientY ?? 0,
          startScrollLeft: container?.scrollLeft ?? 0,
          startScrollTop: container?.scrollTop ?? 0,
        };
      }
    };

    const onTouchMove = event => {
      const touches = event?.touches;
      if ((touches?.length ?? 0) >= 2) {
        event?.preventDefault?.();
        if (!pinchRef?.current?.active) {
          beginPinch(touches);
        }

        const distance = getTouchDistance(touches);
        const startDistance = pinchRef?.current?.startDistance;
        if (!startDistance) {
          return;
        }

        const rawFactor = distance / startDistance;
        const nextScale = clampScale((pinchRef?.current?.startScale ?? 1) * rawFactor);
        const factor = nextScale / (pinchRef?.current?.startScale ?? 1);
        pinchFactorRef.current = factor;
        setPinchFactor(factor);
        applyFocalScroll(factor);
        return;
      }

      if ((touches?.length ?? 0) === 1 && panRef?.current?.active && !pinchRef?.current?.active) {
        const touch = touches?.[0];
        container.scrollLeft =
          (panRef?.current?.startScrollLeft ?? 0) - ((touch?.clientX ?? 0) - (panRef?.current?.startX ?? 0));
        container.scrollTop =
          (panRef?.current?.startScrollTop ?? 0) - ((touch?.clientY ?? 0) - (panRef?.current?.startY ?? 0));
      }
    };

    const onTouchEnd = event => {
      if (pinchRef?.current?.active && (event?.touches?.length ?? 0) < 2) {
        commitPinch();
      }
      if ((event?.touches?.length ?? 0) === 0) {
        panRef.current.active = false;
      }
    };

    const options = { passive: false };
    container.addEventListener('touchstart', onTouchStart, options);
    container.addEventListener('touchmove', onTouchMove, options);
    container.addEventListener('touchend', onTouchEnd, options);
    container.addEventListener('touchcancel', onTouchEnd, options);

    return () => {
      container.removeEventListener('touchstart', onTouchStart, options);
      container.removeEventListener('touchmove', onTouchMove, options);
      container.removeEventListener('touchend', onTouchEnd, options);
      container.removeEventListener('touchcancel', onTouchEnd, options);
    };
  }, []);

  const pinchWidth = pinchBox ? (pinchBox?.width ?? 0) * (pinchFactor ?? 1) : undefined;
  const pinchHeight = pinchBox ? (pinchBox?.height ?? 0) * (pinchFactor ?? 1) : undefined;
  const pinchScale = isPinching ? pinchFactor : undefined;

  return (
    <ViewerRoot data-testid="container-pdf-viewer">
      <ViewerToolbar>
        <button data-testid="button-prev-page" onClick={prevPage} disabled={pageNumber <= 1}>
          <Translate stringId="pdfViewerPreviousPage" />
        </button>
        <button data-testid="button-next-page" onClick={nextPage} disabled={pageNumber >= (numPages ?? -1)}>
          <Translate stringId="pdfViewerNextPage" />
        </button>
        <button
          data-testid="button-zoom-out"
          onClick={zoomOut}
          disabled={(displayedScale ?? 1) <= MIN_SCALE}
          aria-label={translateMessage?.('pdfViewerZoomOut')}
        >
          -
        </button>
        <span data-testid="text-zoom-level">{zoomPercent}%</span>
        <button
          data-testid="button-zoom-in"
          onClick={zoomIn}
          disabled={(displayedScale ?? 1) >= MAX_SCALE}
          aria-label={translateMessage?.('pdfViewerZoomIn')}
        >
          +
        </button>
      </ViewerToolbar>
      <ViewerPageContainer ref={pageContainerRef} data-testid="container-pdf-page">
        <ViewerPageSizer data-testid="container-pdf-page-sizer" pinchWidth={pinchWidth} pinchHeight={pinchHeight}>
          <ViewerPageWrapper ref={pageWrapperRef} data-testid="container-pdf-page-wrapper" pinchScale={pinchScale}>
            <Document file={src} onLoadSuccess={onDocumentLoadSuccess} className="my-react-pdf">
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onRenderSuccess={onPageRenderSuccess}
              />
            </Document>
          </ViewerPageWrapper>
        </ViewerPageSizer>
      </ViewerPageContainer>
      <p data-testid="text-pdf-page-of">{translateMessage?.('pdfViewerPageOf', { pageNumber, numPages })}</p>
    </ViewerRoot>
  );
}
export default Viewer;
